import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  SalesComparisonApproachType,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';

const calculateGarageAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification) ||
    isUndefined(depreciationRatios)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const {
    garage,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const {
    garage: compGarage,
    craftsmanQuality: compDwellingQuality,
    propertyType: compPropertyType = 'singleFamilyResidence',
  } = compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: garage?.spaces,
      compUnitAmount: compGarage?.spaces,
    });
  }

  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (
    isUndefined(quality) ||
    isUndefined(compDwellingQuality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    isUndefined(zipCode) ||
    isUndefined(compZipCode)
  ) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  const subjectArea =
    garage && garage.area && ORDER_FEATURES.garage.hasFeature(subjectImprovements)
      ? AloftArea.schema.parse(garage.area)
      : AloftArea.zero;
  const compArea =
    compGarage && compGarage.area && ORDER_FEATURES.garage.hasFeature(compImprovements)
      ? AloftArea.schema.parse(compGarage.area)
      : AloftArea.zero;

  const adj = adjCalculator.getGarageAdj({
    subject: {
      sqft: subjectArea,
      quality: garage?.quality ?? quality,
      propertyType,
      zipCode,
      garageType: 'attached',
    },
    comp: {
      sqft: compArea,
      quality: compGarage?.quality ?? compDwellingQuality,
      propertyType: compPropertyType,
      zipCode: compZipCode,
      garageType: 'attached',
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;

  return adj;
};

export const selectGarageAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'garage'),
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'garageCarport'),
  ],
  calculateGarageAdj,
);

export const selectGarageAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'garage'),
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'garageCarport'),
  ],
  (
    adjustmentVersion,
    improvements,
    propertyIdentification,
    depreciationRatios,
    compSequence,
    compTypes,
    salesComparisonApproach,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateGarageAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);
